/**
 * Ligne de résultat de l'écran d'ajout — un aliment, personnel ou Open Food Facts.
 *
 * ⚠️ **Les deux origines doivent se distinguer d'un coup d'œil** (specs §6.3-b).
 * La distinction est portée par un **libellé texte** (« Ma base » /
 * « Open Food Facts »), pas par la seule couleur : même règle que pour les
 * jauges de macros — la couleur est un repère, jamais le seul porteur du sens.
 *
 * ⚠️ Fond uni, aucun `backdrop-filter` (§6.3) : ces lignes vivent dans une liste
 * qui défile.
 */

import type { FoodDTO, OffProductDTO, RecipeDTO } from '../../../../shared/types';
import { Badge } from '../../components/nourri';
import { formatMacro } from '../../lib/macros';

function MacroSummary({
  protein,
  carbs,
  fat,
  kcal,
  unit,
}: {
  protein: number;
  carbs: number;
  fat: number;
  kcal: number;
  unit: string;
}) {
  return (
    <span className="n-pick__meta tnum n-ellipsis">
      pour 100 {unit} : {formatMacro(protein, 'g')} P · {formatMacro(carbs, 'g')} G ·{' '}
      {formatMacro(fat, 'g')} L · {formatMacro(kcal, 'kcal')} kcal
    </span>
  );
}

function Row({
  title,
  subtitle,
  badge,
  accent,
  favorite,
  children,
  onClick,
}: {
  title: string;
  subtitle: string | null;
  badge: string;
  accent: boolean;
  favorite?: boolean;
  children: React.ReactNode;
  onClick: () => void;
}) {
  return (
    <button type="button" onClick={onClick} className="n-pick">
      <span className="n-pick__main">
        <span className="n-row" style={{ gap: 6 }}>
          <span className="n-pick__name" style={{ flex: 1 }}>
            {favorite === true && (
              <span aria-label="Favori" title="Favori" style={{ marginRight: 4 }}>
                ★
              </span>
            )}
            {title}
          </span>
          {/*
            ⚠️ **L'origine se lit en toutes lettres** (« Ma base » / « Open Food
            Facts »), jamais à la seule couleur — specs §6.3-b. Le badge teinté
            ne fait que la doubler.
          */}
          <Badge tone={accent ? 'brand' : 'neutral'}>{badge}</Badge>
        </span>
        {subtitle !== null && <span className="n-pick__meta n-ellipsis">{subtitle}</span>}
        {children}
      </span>
    </button>
  );
}

export function PersonalFoodRow({ food, onPick }: { food: FoodDTO; onPick: () => void }) {
  return (
    <Row
      title={food.name}
      subtitle={food.brand}
      badge="Ma base"
      accent
      favorite={food.isFavorite}
      onClick={onPick}
    >
      <MacroSummary {...food.per100} unit={food.baseUnit} />
    </Row>
  );
}

/**
 * Recette de la base personnelle (specs §6.5, §6.3-a).
 *
 * ⚠️ Les macros affichées viennent du serveur, **calculées depuis les
 * ingrédients** — jamais saisies. Elles changent donc si un aliment ingrédient
 * est corrigé, ce qui est exactement le comportement demandé par le §6.5.
 *
 * ⚠️ Troisième badge distinct (« Ma recette »), pour la même raison que les deux
 * autres : la provenance se **lit**, elle ne se devine pas à une couleur.
 */
export function PersonalRecipeRow({
  recipe,
  onPick,
}: {
  recipe: RecipeDTO;
  onPick: () => void;
}) {
  const isPortions = recipe.yieldType === 'portions';
  const perUnit = isPortions ? recipe.perPortion : recipe.per100;

  return (
    <Row
      title={recipe.name}
      subtitle={recipe.tags.length > 0 ? recipe.tags.join(' · ') : null}
      badge="Ma recette"
      accent
      favorite={recipe.isFavorite}
      onClick={onPick}
    >
      <span className="n-pick__meta tnum n-ellipsis">
        {perUnit === null
          ? `${String(recipe.ingredients.length)} ingrédient(s)`
          : `${isPortions ? 'par portion' : 'pour 100 g'} : ${formatMacro(perUnit.protein, 'g')} P · ${formatMacro(perUnit.carbs, 'g')} G · ${formatMacro(perUnit.fat, 'g')} L · ${formatMacro(perUnit.kcal, 'kcal')} kcal`}
      </span>
    </Row>
  );
}

export function OffFoodRow({
  product,
  onPick,
}: {
  product: OffProductDTO;
  onPick: () => void;
}) {
  return (
    <Row
      title={product.name}
      subtitle={product.brand}
      badge="Open Food Facts"
      accent={false}
      onClick={onPick}
    >
      <MacroSummary {...product.per100} unit={product.baseUnit} />
    </Row>
  );
}
